import {assert} from 'Asserts';
import ArgType from 'notebook/tables/excess/ArgType';
import FunctionCategory from 'notebook/tables/excess/FunctionCategory';
import {EvaluablePrimitiveSpreadsheetFunction} from 'notebook/tables/excess/FunctionConstants';
import * as ExcessUtil from 'notebook/tables/excess/Util';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import escapeRegExp from 'util/escapeRegExp';

export default {
  name: 'SUBSTITUTE',
  shortDescription: 'Replaces existing text with new text in a string.',
  category: FunctionCategory.TEXT,
  description: 'Replaces existing text with new text in a string.',
  params: [
    // Custom coercion is required to match Excel's behavior: Number args are coerced to strings using default
    // precision, regardless of any decimal precision specified in the cell's formatting. String args are
    // interpreted literally, without our usual coercion to numbers.
    {
      name: 'text_to_search',
      type: ArgType.singularValue,
      coerce: (value: TypedValue, context?) =>
        formatTypedValue(value, undefined, undefined, undefined, context),
      description: 'The text within which to search and replace.',
    },
    {
      name: 'old_substring',
      type: ArgType.singularValue,
      coerce: (value: TypedValue, context?) =>
        formatTypedValue(value, undefined, undefined, undefined, context),
      description: 'The string to search for within text_to_search.',
    },
    {
      name: 'new_substring',
      type: ArgType.singularValue,
      coerce: (value: TypedValue, context?) =>
        formatTypedValue(value, undefined, undefined, undefined, context),
      description: 'The string that will replace old_substring.',
    },
    {
      name: 'occurrence_number', // 1-indexed
      type: ArgType.positiveInteger, // As a known quirk, if invalid, we raise #NUM while Excel raises #VALUE
      optional: true,
      description:
        'The instance of search_for within text_to_search to replace with new_substring. By default, all occurrences of old_substring are replaced; however, if occurrence_number is specified, only the indicated instance of old_substring is replaced.',
    },
  ],
  returnType: ExcessUtil.VALUE_TYPES.STRING,
  eval: (text: string, oldSubstring: string, newSubstring: string, occurrence?: number): string => {
    // Reference behavior: https://support.office.com/en-us/article/substitute-function-6434944e-a904-4336-a9b0-1e58df3bc332

    if (oldSubstring === '') {
      return text; //  Otherwise str.replace() will prepend newSubstring to text
    }

    let occurrenceIndex = 0;
    return text.replace(
      new RegExp(escapeRegExp(oldSubstring), 'g'), // RegExp is required for multiple matches
      // oxlint-disable-next-line @typescript-eslint/no-explicit-any
      (match: string, ...args: Array<any>): string => {
        assert(args.length === 2, 'Escaped RegExp should never contain capture groups', {text, oldSubstring});
        occurrenceIndex++;
        if (occurrence == null || occurrence === occurrenceIndex) {
          return newSubstring;
        } else {
          return match;
        }
      }
    );
  },
} satisfies EvaluablePrimitiveSpreadsheetFunction;
